import {
	type ApiUploadMethod,
	PostRetrievalAction,
	type Retrieval,
	type RetrievalMethod,
	RetrievalMethodType,
	type SftpPullMethod,
	SftpSelectionStrategy,
	type WebUploadMethod,
} from '@today/types'
import { METHOD_LABELS, METHOD_TYPES, newMethod } from './form-defaults'
import { CheckboxInput, Choice, type FieldErrors, GroupError, NumberInput, TextInput } from './form-fields'

const STRATEGY_OPTIONS = [
	{ id: SftpSelectionStrategy.NEWEST_MATCHING, text: 'The newest matching file' },
	{ id: SftpSelectionStrategy.ALL_NEW, text: 'Every new matching file' },
	{ id: SftpSelectionStrategy.EXACT_NAME, text: 'The file with exactly this name' },
	{ id: SftpSelectionStrategy.SINCE_LAST_SUCCESS, text: 'Every file since the last successful import' },
]

const POST_OPTIONS = [
	{ id: PostRetrievalAction.LEAVE, text: 'Leave it' },
	{ id: PostRetrievalAction.DELETE, text: 'Delete it' },
	{ id: PostRetrievalAction.MOVE, text: 'Move it' },
]

interface MethodProps<M extends RetrievalMethod> {
	method: M
	path: string
	errors: FieldErrors
	onChange: (method: M) => void
}

function SftpPostRetrieval({ method, path, errors, onChange }: MethodProps<SftpPullMethod>) {
	const post = method.postRetrieval
	const setPost = (postRetrieval: SftpPullMethod['postRetrieval']) => onChange({ ...method, postRetrieval })
	return (
		<>
			<Choice
				label="After retrieval"
				path={`${path}.postRetrieval.action`}
				errors={errors}
				value={post.action}
				options={POST_OPTIONS}
				onChange={(action) =>
					setPost({
						action,
						moveToDirectory: action === PostRetrievalAction.MOVE ? (post.moveToDirectory ?? '') : null,
					})
				}
			/>
			{post.action === PostRetrievalAction.MOVE ? (
				<TextInput
					label="Move to directory"
					path={`${path}.postRetrieval.moveToDirectory`}
					errors={errors}
					value={post.moveToDirectory ?? ''}
					onChange={(moveToDirectory) => setPost({ ...post, moveToDirectory })}
				/>
			) : null}
		</>
	)
}

function SftpFields(props: MethodProps<SftpPullMethod>) {
	const { method, path, errors, onChange } = props
	const set = (patch: Partial<SftpPullMethod>) => onChange({ ...method, ...patch })
	const text = (label: string, key: 'connectorId' | 'remoteDirectory' | 'filenamePattern') => (
		<TextInput
			label={label}
			path={`${path}.${key}`}
			errors={errors}
			value={method[key]}
			onChange={(value) => set({ [key]: value })}
		/>
	)
	return (
		<div className="flex flex-col gap-3">
			{text('Connector ID', 'connectorId')}
			{text('Remote directory', 'remoteDirectory')}
			{text('SFTP filename pattern', 'filenamePattern')}
			<Choice
				label="Selection strategy"
				path={`${path}.selectionStrategy`}
				errors={errors}
				value={method.selectionStrategy}
				options={STRATEGY_OPTIONS}
				onChange={(selectionStrategy) => set({ selectionStrategy })}
			/>
			<NumberInput
				label="SFTP minimum age (seconds)"
				path={`${path}.minimumAgeSeconds`}
				errors={errors}
				value={method.minimumAgeSeconds}
				onChange={(minimumAgeSeconds) => set({ minimumAgeSeconds })}
			/>
			<SftpPostRetrieval {...props} />
		</div>
	)
}

function UploadFields({ method, path, errors, onChange }: MethodProps<ApiUploadMethod | WebUploadMethod>) {
	const name = method.type === RetrievalMethodType.API ? 'API' : 'Web'
	const limits = method.limits
	return (
		<div className="flex flex-col gap-3">
			<TextInput
				label={`${name} filename pattern`}
				path={`${path}.filenamePattern`}
				errors={errors}
				value={method.filenamePattern}
				onChange={(filenamePattern) => onChange({ ...method, filenamePattern })}
			/>
			<NumberInput
				label={`${name} max file size (bytes)`}
				path={`${path}.limits.maxFileSizeBytes`}
				errors={errors}
				value={limits.maxFileSizeBytes}
				onChange={(maxFileSizeBytes) => onChange({ ...method, limits: { ...limits, maxFileSizeBytes } })}
			/>
			<NumberInput
				label={`${name} max files per day`}
				path={`${path}.limits.maxFilesPerDay`}
				errors={errors}
				value={limits.maxFilesPerDay}
				onChange={(maxFilesPerDay) => onChange({ ...method, limits: { ...limits, maxFilesPerDay } })}
			/>
		</div>
	)
}

/** Allows or removes a method type; the default follows the methods that remain. */
const toggled = (retrieval: Retrieval, type: RetrievalMethodType, on: boolean): Retrieval => {
	if (on) return { methods: [...retrieval.methods, newMethod(type)], defaultMethod: retrieval.defaultMethod ?? type }
	const methods = retrieval.methods.filter((method) => method.type !== type)
	const keepsDefault = retrieval.defaultMethod !== type
	return { methods, defaultMethod: keepsDefault ? retrieval.defaultMethod : (methods[0]?.type ?? null) }
}

type RetrievalProps = { retrieval: Retrieval; errors: FieldErrors; onChange: (retrieval: Retrieval) => void }

/** One editor per allowed method, at the index it is submitted at. */
function MethodEditors({ retrieval, errors, onChange }: RetrievalProps) {
	const replaceAt = (index: number, method: RetrievalMethod) =>
		onChange({ ...retrieval, methods: retrieval.methods.map((m, i) => (i === index ? method : m)) })
	return retrieval.methods.map((method, index) => {
		const path = `retrieval.methods[${index}]`
		return method.type === RetrievalMethodType.SFTP_PULL ? (
			<SftpFields key={method.type} method={method} path={path} errors={errors} onChange={(m) => replaceAt(index, m)} />
		) : (
			<UploadFields
				key={method.type}
				method={method}
				path={path}
				errors={errors}
				onChange={(m) => replaceAt(index, m)}
			/>
		)
	})
}

/**
 * How the file may arrive (TO-391 spec Contract): one toggle per method type,
 * each allowed method's fields, and the default among them. Method paths use
 * the index the method is submitted at, matching the server's error paths.
 */
export function RetrievalFields({ retrieval, errors, onChange }: RetrievalProps) {
	const has = (type: RetrievalMethodType) => retrieval.methods.some((method) => method.type === type)
	return (
		<fieldset className="flex flex-col gap-3">
			<legend className="text-ds-sm font-semibold text-ds-text">How the file arrives</legend>
			{METHOD_TYPES.map((type) => (
				<CheckboxInput
					key={type}
					label={`Allow ${METHOD_LABELS[type]}`}
					checked={has(type)}
					onChange={(on) => onChange(toggled(retrieval, type, on))}
				/>
			))}
			<GroupError path="retrieval.methods" errors={errors} />
			<MethodEditors retrieval={retrieval} errors={errors} onChange={onChange} />
			{retrieval.defaultMethod ? (
				<Choice
					label="Default method"
					path="retrieval.defaultMethod"
					errors={errors}
					value={retrieval.defaultMethod}
					options={retrieval.methods.map((method) => ({ id: method.type, text: METHOD_LABELS[method.type] }))}
					onChange={(defaultMethod) => onChange({ ...retrieval, defaultMethod })}
				/>
			) : null}
		</fieldset>
	)
}
